import React, { useEffect, useState } from "react";
import BaseSearchPage from "../baseSearchPage";
import PackageSearchResultItem from "./packageSearchResultItem";
import { useApi } from "/src/helpers/Hooks/useApi";
import { addDays, parse, format } from "date-fns";

const searchFormModelBuilder = (searchParams) => {
    const duration = parseInt(searchParams.duration);
    const start = parse(searchParams.earlyStart, "yyyy-MM-dd", new Date());
    const end = addDays(start, duration);

    return {
        dep: searchParams.from.split("-"),
        to: { code: searchParams.to, name: "unknwon" },
        startDate: format(start, "yyyy-MM-dd"),
        endDate: format(end, "yyyy-MM-dd"),
        pax: searchParams.rooms,
    };
};

// villa: the Villa + Flights results page (/VF/) - the same search and cards, villa rooms only, one party (Almanac).
export default function PackageSearchPage({ villa = false }) {
    const apiClient = useApi();
    const fetcher = async (searchParams, filterParams, minRank, recommendedExhausted) => {
        const apiSearchParams = new URLSearchParams();
        apiSearchParams.append("from", searchParams.from);
        apiSearchParams.append("to", searchParams.to);
        apiSearchParams.append("durations", searchParams.duration);
        apiSearchParams.append("earlyStart", searchParams.earlyStart);
        apiSearchParams.append("lateStart", searchParams.lateStart);

        apiSearchParams.append("lateStart", searchParams.lateStart);
        apiSearchParams.append("rooms", searchParams.rooms);
        if (filterParams["rating"]) {
            apiSearchParams.append("fRatings", filterParams["rating"]);
        }
        if (filterParams["trip-advisor"]) {
            apiSearchParams.append("fMinTripAdvisor", filterParams["trip-advisor"]);
        }

        if (filterParams.facilities) {
            apiSearchParams.append("fFacilities", filterParams.facilities);
        }
        if (filterParams["board-basis"]) {
            apiSearchParams.append("fBoardBases", filterParams["board-basis"]);
        }
        // Villa + Flights only (the AlmanacAPI test bed): airline, flight time, the Include Transfers price lens
        // and its journey type, offers and the debug-only supplier filter. Holidays (Dealfinder / Almanac facade)
        // has none of them yet.
        if (villa) {
            if (filterParams["flight-duration-min"]) {
                apiSearchParams.append("fFlightDurationMin", filterParams["flight-duration-min"]);
            }
            if (filterParams["flight-duration-max"]) {
                apiSearchParams.append("fFlightDurationMax", filterParams["flight-duration-max"]);
            }
            if (filterParams["airlines"]) {
                apiSearchParams.append("fAirlines", filterParams["airlines"]);
            }
            if (filterParams["transfers-included"]) {
                apiSearchParams.append("fTransfersIncluded", "true");
            }
            // Journey type refines WHICH transfer the Include Transfers lens prices in, so it only applies when
            // that toggle is on - matching where the control is shown, and stopping a stale transfer-type in a
            // shared URL from filtering with nothing on screen to explain it.
            if (
                filterParams["transfers-included"] &&
                filterParams["transfer-type"] &&
                filterParams["transfer-type"] !== "any"
            ) {
                apiSearchParams.append("fTransferType", filterParams["transfer-type"]);
            }
            if (filterParams["offers"]) {
                apiSearchParams.append("fOffers", filterParams["offers"]);
            }
            if (filterParams["property-source"]) {
                apiSearchParams.append("fSources", filterParams["property-source"]);
            }
        }
        // Merged Destination facet (mixed country/region/resort codes). The legacy
        // per-level params below still work for old shared URLs.
        if (filterParams["destinations"]) {
            apiSearchParams.append("fDestinations", filterParams["destinations"]);
        }
        if (filterParams["countries"]) {
            apiSearchParams.append("fCountries", filterParams["countries"]);
        }
        if (filterParams["regions"]) {
            apiSearchParams.append("fRegions", filterParams["regions"]);
        }
        if (filterParams["resorts"]) {
            apiSearchParams.append("fResorts", filterParams["resorts"]);
        }

        if (filterParams.sort) {
            apiSearchParams.append("sort", filterParams.sort);
        }
        if (minRank) {
            apiSearchParams.append("minRank", minRank);
        }
        if (recommendedExhausted) {
            apiSearchParams.append("recommendedExhausted", "true");
        }
        const postsData = villa
            ? await apiClient.doVillaFlightsSearch(apiSearchParams)
            : await apiClient.doPackageSearch(apiSearchParams);

        return postsData;
    };

    const searchFormModelBuilder = (searchParams) => {
        const duration = parseInt(searchParams.duration);
        const start = parse(searchParams.earlyStart, "yyyy-MM-dd", new Date());
        const end = addDays(start, duration);

        const search = {
            dep: searchParams.from.split("-"),
            to: {
                code: searchParams.to,
                name: "unknwon",
            },
            startDate: format(start, "yyyy-MM-dd"),
            endDate: format(end, "yyyy-MM-dd"),
            pax: searchParams.rooms,
            // reopens the search form on the Villas tab (Villa + Flights) rather than Holidays
            ...(villa ? { type: "villa-flights" } : {}),
        };

        return search;
    };

    return (
        <BaseSearchPage
            resultFetcher={fetcher}
            searchFormBuilder={searchFormModelBuilder}
            resultTemplate={(x) => <PackageSearchResultItem result={x} />}
            listName={villa ? "Villa + Flights Results" : undefined}
            showFlightDurationFilter={villa}
            showExtraFilters={villa}
        ></BaseSearchPage>
    );
}
